import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Low-poly car bodies for parked cars and traffic (instances): centred on the origin in metres, x
 * forward, y up, z across. Body panels are white so instanceColor paints them; glass, tyres and lights
 * keep their own colours (tinted a little by the paint, which reads as reflections).
 */
const GLASS = new THREE.Color(0.08, 0.1, 0.13);
const TYRE = new THREE.Color(0.05, 0.05, 0.05);
const LAMP = new THREE.Color(1, 0.97, 0.85);
const TAIL = new THREE.Color(0.6, 0.05, 0.04);
const WHITE = new THREE.Color(1, 1, 1);
const TRIM = new THREE.Color(0.12, 0.12, 0.13);

function box(sx: number, sy: number, sz: number, x: number, y: number, z: number, c: THREE.Color): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(sx, sy, sz).translate(x, y, z).toNonIndexed();
  return colour(g, c);
}

/** Mark the paintable parts (white) for the car paint shader (models/materials.ts). */
function paintable(g: THREE.BufferGeometry, on: boolean): THREE.BufferGeometry {
  g.setAttribute('carPaint', new THREE.BufferAttribute(new Float32Array(g.getAttribute('position').count).fill(on ? 1 : 0), 1));
  return g;
}

function colour(g: THREE.BufferGeometry, c: THREE.Color): THREE.BufferGeometry {
  g.deleteAttribute('uv');
  const n = g.getAttribute('position').count;
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) c.toArray(a, i * 3);
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  return g;
}

interface Body {
  length: number;
  width: number;
  /** Lower body: bottom, top. Cabin: length, back offset from centre, top. */
  low: [number, number];
  cabin: [number, number, number];
  wheelBase: number;
}

const BODIES: Body[] = [
  { length: 4.5, width: 1.8, low: [0.3, 1.0], cabin: [2.3, -0.25, 1.48], wheelBase: 1.35 }, // sedan
  { length: 3.9, width: 1.72, low: [0.3, 0.98], cabin: [2.3, -0.55, 1.5], wheelBase: 1.2 }, // hatchback
  { length: 4.9, width: 1.95, low: [0.36, 1.15], cabin: [3.3, -0.55, 2.0], wheelBase: 1.55 }, // van
];

export function carBodyGeometry(type: number): THREE.BufferGeometry {
  const b = BODIES[type % BODIES.length];
  const [y0, y1] = b.low;
  const [cl, cx, cy] = b.cabin;
  const hl = b.length / 2;
  const hw = b.width / 2;
  const parts = [
    box(b.length, y1 - y0, b.width, 0, (y0 + y1) / 2, 0, WHITE),
    box(cl, cy - y1, b.width * 0.92, cx, (y1 + cy) / 2, 0, GLASS),
    box(cl * 0.96, 0.05, b.width * 0.9, cx, cy + 0.02, 0, WHITE), // roof panel
    box(0.06, 0.12, 0.3, hl + 0.01, y1 - 0.15, hw * 0.65, LAMP),
    box(0.06, 0.12, 0.3, hl + 0.01, y1 - 0.15, -hw * 0.65, LAMP),
    box(0.06, 0.12, 0.28, -hl - 0.01, y1 - 0.12, hw * 0.7, TAIL),
    box(0.06, 0.12, 0.28, -hl - 0.01, y1 - 0.12, -hw * 0.7, TAIL),
    box(0.1, 0.14, b.width * 0.98, hl + 0.02, y0 + 0.12, 0, TRIM), // bumpers
    box(0.1, 0.14, b.width * 0.98, -hl - 0.02, y0 + 0.12, 0, TRIM),
  ];
  for (const sx of [-1, 1])
    for (const sz of [-1, 1]) {
      const w = new THREE.CylinderGeometry(0.33, 0.33, 0.22, 6).rotateX(Math.PI / 2).translate(sx * b.wheelBase, 0.33, sz * (hw - 0.1)).toNonIndexed();
      parts.push(colour(w, TYRE));
    }
  for (const p of parts) {
    const c = p.getAttribute('color');
    paintable(p, c.getX(0) === 1 && c.getY(0) === 1 && c.getZ(0) === 1);
  }
  const g = mergeGeometries(parts)!;
  for (const p of parts) p.dispose();
  return g;
}

/** Car paint: instanceColor tints the white body. */
export function carMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.35, metalness: 0.4, envMapIntensity: 1.5 });
}
